/* Inviting, accepting and removing — STORY-007. */
import { z } from 'zod';
export const inviteMemberRequestSchema = z.object({
    email: z.string().trim().min(3).max(320).email(),
    /* The Owner chooses what it grants, so a co-founder can be invited straight
     * in as an Owner — STORY-006. */
    role: z.enum(['owner', 'member']),
});
/**
 * An invitation as the team screen sees it.
 *
 * The token is never here in any form. The CODE is, because an Owner reading it
 * down a phone is the whole reason it exists — and it is useless without the
 * address beside it.
 */
export const invitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: z.string(),
    code: z.string(),
    expiresAt: z.string(),
});
export const invitationsResponseSchema = z.object({
    invitations: z.array(invitationSchema),
});
/**
 * An invitation as its RECIPIENT sees it, on the workspace chooser — STORY-004.
 *
 * Deliberately not `invitationSchema`. That one is the Owner's view of what a
 * workspace has sent and carries the `code`; this is somebody's own pending
 * invitation, and the code would be a credential travelling in a response that
 * nothing needs it in. The id is enough to accept with, and accepting still
 * checks the address.
 *
 * The workspace name is the point of the row — "you were invited" naming no
 * workspace is not something a person can act on.
 */
export const receivedInvitationSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    organisationName: z.string(),
    role: z.string(),
    expiresAt: z.string(),
});
export const receivedInvitationsResponseSchema = z.object({
    invitations: z.array(receivedInvitationSchema),
});
/**
 * Accepting, by any one of three credentials.
 *
 * Exactly one of `token`, `code` or `invitationId`. The token comes from an
 * emailed link; the code is typed by somebody who was told it; the id comes
 * from the chooser's own list of invitations addressed to the signed-in User,
 * added by STORY-004 so a person who can already SEE the invitation does not
 * have to go and find the email to retype what it says.
 *
 * Separate fields rather than one, because they are separate strengths: the
 * code is short enough to say aloud and is therefore rate-limited harder, and
 * the id is not a secret at all. **The id is safe precisely because it is not
 * treated as one** — every path is bound to the invited address, so an id
 * guessed by a stranger resolves to an invitation that refuses them.
 */
export const acceptInvitationRequestSchema = z
    .object({
    token: z.string().optional(),
    code: z.string().optional(),
    invitationId: z.string().uuid().optional(),
})
    .refine((value) => [value.token, value.code, value.invitationId].filter((given) => Boolean(given)).length === 1, { message: 'Present exactly one of token, code, or invitationId.' });
export const acceptInvitationResponseSchema = z.object({
    organisationId: z.string(),
    name: z.string(),
    role: z.string(),
});
//# sourceMappingURL=invitations.js.map